// src/components/TopNav.js
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { colors, radii, buttonPrimary, primaryHover } from "../styles/designTokens";

const NAV_ITEMS = [
  { key: "myEvents", to: "/my-events", icon: "🎟️", label: "My Events" },
  { key: "connections", to: "/connections", icon: "🤝", label: "Connections" },
  { key: "circles", to: "/circles", icon: "🎉", label: "Hangouts" },
  { key: "stayInTouch", to: "/stay-in-touch", icon: "⏰", label: "Stay In Touch" },
  { key: "wishlist", to: "/wishlist", icon: "🎁", label: "Wishlist" },
  { key: "myProfile", to: "/edit-profile", icon: "👤", label: "My Profile" },
];

/**
 * Slim top bar shown on every subscriber-only page (My Events, Connections,
 * Circles, Stay In Touch, Wishlist, Edit Profile). Its whole reason for
 * existing is the "Events" link: connections/circles/stay-in-touch/wishlist
 * are all free-to-use-once-subscribed features that don't themselves book
 * anything, so every one of those pages needs a constant, unmissable way
 * back to the paid events flow - this is that.
 *
 * The inner content is capped to the same width used by the pages below it
 * (not full-bleed) so the bar doesn't look like a disconnected, over-wide
 * strip sitting above a much narrower centered page. Laid out as two rows
 * instead of one horizontally-scrolling row: five nav items + logo + CTA
 * button doesn't reliably fit one row below ~1000px, and a scrolling row
 * hid options off-screen with no indication they existed beyond a faint
 * edge fade. Row two wraps (rather than scrolls) if a viewport is narrow
 * enough that even five items on their own don't fit, so nothing is ever
 * hidden off-screen.
 *
 * Rendered once by SubscriberLayout above the routed page content, so it
 * stays mounted across navigations between these pages instead of
 * unmounting/remounting on every click - hence reading the active item
 * straight from the current route rather than taking it as a prop.
 */
const TopNav = () => {
  const location = useLocation();
  const [hovered, setHovered] = useState(null);

  const linkStyle = (isActive, isHovered) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: isActive ? colors.primaryDark : colors.body,
    backgroundColor: isActive ? colors.primaryTint : isHovered ? colors.surfaceMuted : "transparent",
    fontWeight: isActive ? "700" : "600",
    fontSize: "0.82rem",
    textDecoration: "none",
    padding: "6px 12px",
    borderRadius: radii.pill,
    transition: "background-color 0.15s ease, color 0.15s ease",
    whiteSpace: "nowrap",
  });

  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.88)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: `0 1px 0 ${colors.border}`,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <style>{`
        @media (max-width: 860px) {
          .topnav-item{padding:6px 9px !important; font-size:0.78rem !important; gap:4px !important}
        }
      `}</style>
      <div
        style={{
          maxWidth: "980px",
          margin: "0 auto",
          padding: "9px 20px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "14px",
          }}
        >
          <Link
            to="/my-events"
            style={{
              fontWeight: "800",
              textDecoration: "none",
              fontSize: "1.05rem",
              flexShrink: 0,
              background: colors.gradientPrimary,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            OkGather
          </Link>

          <Link
            to="/events"
            style={{ ...buttonPrimary, padding: "8px 18px", fontSize: "0.82rem", flexShrink: 0 }}
            {...primaryHover}
          >
            🎉 Events
          </Link>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "4px",
            marginTop: "8px",
          }}
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className="topnav-item"
              title={item.label}
              style={linkStyle(location.pathname === item.to, hovered === item.key)}
              onMouseEnter={() => setHovered(item.key)}
              onMouseLeave={() => setHovered(null)}
            >
              <span aria-hidden="true">{item.icon}</span>
              <span className="topnav-label">{item.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopNav;
